// Phase 73: resolve the cart-line variant id from variant_axis custom-
// field picks. Walks every field with fieldRole === "variant_axis",
// collects the picked option's `variantImpact.{axisName, value}` into
// an axis-value map, then intersects that map against the product's
// variants array (sourced from /products/<handle>.js by embed.js).
//
// Returns null when:
//   - no variant_axis fields exist on the template (no override needed)
//   - any variant_axis field has no pick yet (incomplete - fall back
//     to the customer's theme-side variant pick)
//   - no variant matches the intersection (admin misconfigured)
//
// Falls back to `defaultVariantId` (the variant the customer picked
// in the theme's native size/colour picker) in any of those cases, so
// add-to-cart never breaks because of a configurator gap.

import type { CustomField } from "../types";

/** Subset of /products/<handle>.js shape the resolver needs. */
export type ProductJson = {
  options?: Array<string | { name: string }>;
  variants?: Array<{
    id: number | string;
    option1?: string | null;
    option2?: string | null;
    option3?: string | null;
  }>;
};

export function resolveVariantId(
  customFields: CustomField[] | undefined,
  fieldValues: Record<string, string | string[]>,
  productJson: ProductJson | null,
  defaultVariantId: string | null,
): string | null {
  if (!productJson || !productJson.variants || productJson.variants.length === 0) {
    return defaultVariantId;
  }
  const variantAxisFields = (customFields ?? []).filter(
    (f) => f.fieldRole === "variant_axis",
  );
  if (variantAxisFields.length === 0) return defaultVariantId;

  // Build the picked axis-value map. Skip fields whose pick has no
  // variantImpact attached (admin set fieldRole=variant_axis but didn't
  // wire the options yet) so a half-configured field doesn't lock the
  // customer out of add-to-cart.
  const pickedByAxis: Record<string, string> = {};
  for (const f of variantAxisFields) {
    const v = fieldValues[f.slug];
    if (typeof v !== "string" || !v) continue;
    const opt = f.options.find((o) => o.value === v);
    const impact = opt?.variantImpact;
    if (!impact || !impact.axisName || !impact.value) continue;
    pickedByAxis[impact.axisName] = impact.value;
  }
  // No usable picks yet - stay on the default variant.
  if (Object.keys(pickedByAxis).length === 0) return defaultVariantId;

  // The product's option names are in display order; option1/2/3 on
  // each variant map to options[0..2] respectively. Accept either
  // `{ name }` shape (rare) or a bare string (Shopify's storefront JS).
  const axisNames = (productJson.options ?? []).map((o) =>
    typeof o === "string" ? o : o?.name ?? "",
  );

  // For each variant, check that every picked axis matches. Case-
  // insensitive comparison since admin-typed axis names + values often
  // mismatch case ("Color" vs "color", "Forest" vs "forest").
  const lowerPicked: Record<string, string> = Object.fromEntries(
    Object.entries(pickedByAxis).map(([k, v]) => [k.toLowerCase(), v.toLowerCase()]),
  );
  const match = productJson.variants.find((variant) => {
    for (const [axisLower, valueLower] of Object.entries(lowerPicked)) {
      const idx = axisNames.findIndex((n) => n.toLowerCase() === axisLower);
      if (idx === -1) return false; // axis isn't on this product
      const variantValue = [
        variant.option1,
        variant.option2,
        variant.option3,
      ][idx];
      if (!variantValue) return false;
      if (String(variantValue).toLowerCase() !== valueLower) return false;
    }
    return true;
  });

  if (!match) return defaultVariantId; // no variant matches - admin misconfig
  return String(match.id);
}
